/* eslint-disable no-console */
import { inject, Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import {
  BehaviorSubject,
  Subject,
  filter,
  takeUntil,
  Observable,
  tap,
} from 'rxjs';
import { b2cPolicies, loginRequest } from './auth-config';
import { environment } from 'src/environment/environment';
import { HttpClient } from '@angular/common/http';
import { environment as env } from 'src/environment/environment';

/**
 * AuthService class provides authentication functionality for the application.
 * It uses MSAL library for authentication and authorization.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  localStorageJwtKey = 'BCS_JWT';

  tokenExpiredDate!: Date;

  private readonly _destroying$ = new Subject<void>();

  isAuthorized$ = new BehaviorSubject({ isAuth: false, isLoading: false });
  readonly #http = inject(HttpClient);

  loginDisplay = false;

  azureUser: AccountInfo | undefined | null = JSON.parse(
    localStorage.getItem('azure_user') as string
  );

  accessToken: string | undefined = '';

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public router: Router
  ) {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        const account = this.getActiveAccount();

        if (!account) {
          // this.router.navigate(['/login']);
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims;
        if (
          (idtoken as any)['acr'] === b2cPolicies.names.signUpSignIn ||
          (idtoken as any)['tfp'] === b2cPolicies.names.signIn
        ) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        if (
          (idtoken as any)['tfp'] === b2cPolicies.names.resetPassword ||
          (idtoken as any)['tfp'] === b2cPolicies.names.signIn
        ) {
          return this.logout();
        }

        /**
         * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
         * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
         * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        if (
          (idtoken as any)['acr'] === b2cPolicies.names.editProfile ||
          (idtoken as any)['tfp'] === b2cPolicies.names.editProfile
        ) {
          // retrieve the account from initial sing-in to the app
          const originalSignInAccount = this.msalService.instance
            .getAllAccounts()
            .find(
              account =>
                account.idTokenClaims?.oid === (idtoken as any)['oid'] &&
                account.idTokenClaims?.sub === (idtoken as any)['sub'] &&
                (account.idTokenClaims?.['acr'] ===
                  b2cPolicies.names.signUpSignIn ||
                  account.idTokenClaims?.['tfp'] ===
                    b2cPolicies.names.signUpSignIn)
            );

          const signUpSignInFlowRequest: SsoSilentRequest = {
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            account: originalSignInAccount,
          };

          // silently login again with the signUpSignIn policy
          this.msalService.ssoSilent(signUpSignInFlowRequest);
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         * If you do not want this behavior and prefer your users to stay signed in instead,
         * you can replace the code below with the same pattern used for handling the return from
         * profile edit flow (see above ln. 74-92).
         */
        if (
          (idtoken as any)['acr'] === b2cPolicies.names.resetPassword ||
          (idtoken as any)['tfp'] === b2cPolicies.names.resetPassword
        ) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        if (payload.account) {
          this.isAuthorized$.next({ isAuth: true, isLoading: false });
          this.router.navigate(['/patients']);
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = <AuthenticationResult>result.payload;

        if (
          payload &&
          ((payload.idTokenClaims as any)['tfp'] ===
            b2cPolicies.names.resetPassword ||
            (payload.idTokenClaims as any)['acr'] ===
              b2cPolicies.names.signUpSignIn)
        ) {
          return;
        }

        // Checking for the forgot password error. Learn more about B2C error codes at
        // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          this.loginRedirect(b2cPolicies.authorities.resetPassword);
        }

        this.logout();
      });
  }

  refreshIdToken(url?: string) {
    const silentRequest: any = {
      scopes: ['openid', 'profile'],
      account: this.getActiveAccount(),
    };

    this.msalService.acquireTokenSilent(silentRequest).subscribe({
      next: response => {
        this.accessToken = response.idToken;
        localStorage.setItem(this.localStorageJwtKey, response.idToken);
        if (url) {
          const routes = url.split('/').filter(item => Boolean(item));
          this.router.navigate(['/', ...routes]);
        }
      },
    });
  }

  /**
   * Sets the login display based on the number of accounts returned by MSAL.
   */
  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Retrieves the active account from the MSAL service instance. If there is no active account, it retrieves all accounts and sets the first one as active.
   * If the token of the active account has expired, it redirects to the login page.
   * If there is no active account, it sets the isAuthorized$ observable to false.
   * @returns The active account object or null if there is no active account.
   */
  getActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        activeAccount = accounts[0];
        this.msalService.instance.setActiveAccount(activeAccount);
      }
    }

    if (activeAccount?.idTokenClaims?.['exp']) {
      this.tokenExpiredDate = new Date(
        activeAccount.idTokenClaims['exp'] * 1000
      );

      // Якщо токен прострочений → пробуємо silent refresh
      if (this.tokenExpiredDate < new Date()) {
        const silentRequest: SsoSilentRequest = {
          authority: b2cPolicies.authorities.signUpSignIn.authority,
          account: activeAccount,
          scopes: loginRequest.scopes,
        };

        this.msalService.ssoSilent(silentRequest).subscribe({
          next: (result: any) => {
            this.msalService.instance.setActiveAccount(result.account);
            this.isAuthorized$.next({ isAuth: true, isLoading: false });
          },
          error: (err: any) => {
            if (err instanceof InteractionRequiredAuthError) {
              // якщо silent не вийшов → interactive redirect
              this.loginRedirect();
            } else {
              console.error('Silent refresh error:', err);
            }
          },
        });
      }
    }

    // Якщо аккаунт є і ми ще не оновили статус
    if (activeAccount && !this.isAuthorized$.getValue().isAuth) {
      this.isAuthorized$.next({ isAuth: true, isLoading: false });
    }

    // Якщо зовсім немає акаунту → йдемо в loginRedirect
    if (!activeAccount) {
      this.msalService.loginRedirect(loginRequest);
    }

    return activeAccount;
  }

  /**
   * Retrieves the user's access token from local storage, if available.
   * If the access token is not already stored in memory, it will be retrieved from local storage and stored in memory for future use.
   * @returns The user's access token, or an empty string if it is not available.
   */
  getUserToken() {
    if (!this.accessToken) {
      this.accessToken = localStorage.getItem(this.localStorageJwtKey) || '';
    }

    return this.accessToken;
  }

  /**
   * Initiates a login redirect flow using MSAL.
   * @param userFlowRequest Optional parameter to specify additional login request parameters.
   */
  loginRedirect(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        ...userFlowRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect(userFlowRequest);
    }
  }

  /**
   * Initiates the login process using the MSAL service.
   */
  login() {
    this.msalService.loginRedirect(loginRequest);
  }

  /**
   * Logs out the user by clearing the local storage, logging out of the MSAL service,
   * and setting the `isAuthorized$` subject to `{ isAuth: false, isLoading: true }`.
   * The user's language preference is saved before clearing the local storage and restored afterwards.
   */
  logout(): Observable<void> {
    return this.#http.post<void>(`${env.apiGateway}/login/logout`, {}).pipe(
      tap(() => {
        this.azureUser = undefined;
        const lang = localStorage.getItem('ml_lang') || 'es'; // save lang before clear()
        localStorage.clear();
        localStorage.setItem('ml_lang', lang);
        this.msalService.logout();
      })
    );
  }

  /**
   * Handles the redirect observable for the MSAL service.
   * @returns An observable that emits the result of the redirect operation.
   */
  handleRedirectObservable() {
    return this.msalService.handleRedirectObservable();
  }

  /**
   * Returns the Azure user object if it exists, otherwise returns undefined.
   * If the user object does not exist, it will attempt to retrieve it from the MSAL service instance.
   * @returns The Azure user object or undefined.
   */
  getAzureUser() {
    if (!this.azureUser) {
      const users = this.msalService.instance.getAllAccounts();
      if (users.length > 0) {
        this.azureUser =
          users.find(
            u => u.idTokenClaims?.aud === environment.AZURE_APP_CLIENT_ID
          ) || undefined;
      }
    }

    return this.azureUser;
  }

  /**
   * Saves the user object and access token to local storage.
   * @param userObj - The user object containing the account and id token.
   */
  saveUser(userObj: AuthenticationResult) {
    this.azureUser = userObj.account;
    this.accessToken = userObj.idToken;

    if (this.azureUser?.idTokenClaims?.auth_time) {
      localStorage.setItem(
        'exp_tkn_time',
        (
          this.azureUser.idTokenClaims.auth_time * 1000 +
          8 * 1000 * 60 * 60
        ).toString()
      );
    }

    localStorage.setItem('azure_user', JSON.stringify(this.azureUser));
    localStorage.setItem(this.localStorageJwtKey, this.accessToken);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
